import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Hero from "../components/landing/Hero";
import {
  FiArrowRight,
  FiCode,
  FiCheckCircle,
  FiDatabase,
  FiZap,
  FiLayers,
  FiTarget,
  FiExternalLink,
} from "react-icons/fi";
import {
  fetchLatestSnapshot,
  flattenSnapshotConfig,
} from "../services/snapshotService";
import { supabase } from "../services/supabase";
import { useAuth } from "../context/AuthContext";

const Landing = ({ onAuthClick }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [benchmarkSets, setBenchmarkSets] = useState([]);
  const [totalRequests, setTotalRequests] = useState(0);
  const [statsLoaded, setStatsLoaded] = useState(false);

  const handleStartBenchmarking = () => {
    if (user) {
      navigate("/run");
    } else {
      onAuthClick("signin", "/run");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setStatsLoaded(false);

      // Load benchmark sets from the latest dataset snapshot (same source as Benchmark Sets page)
      const { data: snapshot, error } = await fetchLatestSnapshot();
      if (snapshot && !error) {
        setBenchmarkSets(flattenSnapshotConfig(snapshot));
        setStatsLoaded(true);
      } else {
        console.error("Error loading benchmark sets:", error);
      }

      // Fetch total request count
      const { count, error: countError } = await supabase
        .from("bm_requests")
        .select("*", { count: "exact", head: true });

      if (!countError && count !== null) {
        setTotalRequests(count);
      }
    };

    loadData();
  }, []);

  const uniqueSubjects = [...new Set(benchmarkSets.map((s) => s.subject))];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <Hero
        onAuthClick={onAuthClick}
        benchmarkSetCount={benchmarkSets.length}
        subjectCount={uniqueSubjects.length}
        loadingStats={!statsLoaded}
      />

      {/* How It Works Section */}
      <section className="relative bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-primary-600">
              How it works
            </p>
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Simple, powerful benchmarking
            </h2>
            <p className="mt-4 text-lg text-gray-500">
              Everything you need to evaluate and improve your content
              generators
            </p>
          </div>

          {/* Three Key Concepts */}
          <div className="grid gap-8 lg:grid-cols-3">
            {/* Generators Card */}
            <div className="group relative rounded-3xl bg-gradient-to-b from-gray-50 to-white p-8 transition-all duration-300 hover:shadow-xl">
              <div className="absolute inset-0 rounded-3xl border border-gray-200 transition-colors duration-300 group-hover:border-primary-200" />
              <div className="relative">
                <div className="mb-6 inline-flex size-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg">
                  <FiCode className="size-6 text-white" />
                </div>
                <h3 className="mb-3 text-xl font-semibold text-gray-900">
                  Content Generators
                </h3>
                <p className="text-gray-500">
                  Teams building AI-powered content generators that create
                  educational materials aligned with curriculum standards.
                </p>
              </div>
            </div>

            {/* Evaluator Card */}
            <div className="group relative rounded-3xl bg-gradient-to-b from-gray-50 to-white p-8 transition-all duration-300 hover:shadow-xl">
              <div className="absolute inset-0 rounded-3xl border border-gray-200 transition-colors duration-300 group-hover:border-primary-200" />
              <div className="relative">
                <div className="mb-6 inline-flex size-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-blue-600 shadow-lg">
                  <FiCheckCircle className="size-6 text-white" />
                </div>
                <h3 className="mb-3 text-xl font-semibold text-gray-900">
                  InceptBench Evaluator
                </h3>
                <p className="mb-4 text-gray-500">
                  Our standardized evaluation framework that scores content
                  quality, pedagogical alignment, and accuracy.
                </p>
                <a
                  href="https://inceptbench.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                >
                  Learn more
                  <FiArrowRight className="size-3.5" />
                </a>
              </div>
            </div>

            {/* Benchmark Suite Card */}
            <div className="group relative rounded-3xl bg-gradient-to-b from-gray-50 to-white p-8 transition-all duration-300 hover:shadow-xl">
              <div className="absolute inset-0 rounded-3xl border border-gray-200 transition-colors duration-300 group-hover:border-primary-200" />
              <div className="relative">
                <div className="mb-6 inline-flex size-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg">
                  <FiDatabase className="size-6 text-white" />
                </div>
                <h3 className="mb-3 text-xl font-semibold text-gray-900">
                  Benchmark Datasets
                </h3>
                <p className="text-gray-500">
                  Curated sets of content generation requests spanning K-12,
                  multiple subjects, and Common Core standards.
                </p>
              </div>
            </div>
          </div>

          {/* Process Flow */}
          <div className="mt-20">
            <div className="mx-auto max-w-4xl">
              <div className="relative flex flex-wrap items-center justify-center gap-4 sm:gap-6">
                {/* Step 1 */}
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-full bg-gray-100 text-sm font-bold text-gray-600">
                    1
                  </div>
                  <span className="font-medium text-gray-700">
                    Configure Suite
                  </span>
                </div>
                <FiArrowRight className="hidden size-5 text-gray-300 sm:block" />

                {/* Step 2 */}
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-full bg-gray-100 text-sm font-bold text-gray-600">
                    2
                  </div>
                  <span className="font-medium text-gray-700">
                    Run Requests
                  </span>
                </div>
                <FiArrowRight className="hidden size-5 text-gray-300 sm:block" />

                {/* Step 3 */}
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-full bg-gray-100 text-sm font-bold text-gray-600">
                    3
                  </div>
                  <span className="font-medium text-gray-700">
                    Evaluate Content
                  </span>
                </div>
                <FiArrowRight className="hidden size-5 text-gray-300 sm:block" />

                {/* Step 4 */}
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-full bg-primary-600 text-sm font-bold text-white">
                    4
                  </div>
                  <span className="font-semibold text-gray-900">
                    Get Scores
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Flow Diagram */}
          <div className="mt-16 flex justify-center">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-50/50 p-2 shadow-sm">
              <img
                src="/benchmark_flow.png"
                alt="Benchmark Flow Diagram"
                className="max-w-full rounded-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benchmark Sets Section */}
      <section className="relative bg-gray-50 py-24 sm:py-32">
        {/* Subtle pattern */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="mb-12 text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-primary-600">
              Datasets
            </p>
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Comprehensive benchmark coverage
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-500">
              Explore our curated benchmark datasets aligned with Common Core
              standards across different grades and subjects.
            </p>
          </div>

          {/* Stats Grid */}
          {statsLoaded && benchmarkSets.length > 0 && (
            <div className="mb-12 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
              {/* Stat Card 1 */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="mb-3 inline-flex size-11 items-center justify-center rounded-xl bg-primary-100">
                  <FiLayers className="size-5 text-primary-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {benchmarkSets.length}
                </div>
                <div className="mt-1 text-sm text-gray-500">Benchmark Sets</div>
              </div>

              {/* Stat Card 2 */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="mb-3 inline-flex size-11 items-center justify-center rounded-xl bg-emerald-100">
                  <FiTarget className="size-5 text-emerald-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {uniqueSubjects.length}
                </div>
                <div className="mt-1 text-sm text-gray-500">Subjects</div>
              </div>

              {/* Stat Card 3 */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="mb-3 inline-flex size-11 items-center justify-center rounded-xl bg-violet-100">
                  <FiDatabase className="size-5 text-violet-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {totalRequests.toLocaleString()}
                </div>
                <div className="mt-1 text-sm text-gray-500">Total Requests</div>
              </div>

              {/* Stat Card 4 */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="mb-3 inline-flex size-11 items-center justify-center rounded-xl bg-amber-100">
                  <FiZap className="size-5 text-amber-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900">K-12</div>
                <div className="mt-1 text-sm text-gray-500">Grade Range</div>
              </div>
            </div>
          )}

          {/* CTA Button */}
          <div className="text-center">
            <Link
              to="/benchmark-sets"
              className="group inline-flex items-center gap-2 rounded-full bg-gray-900 px-6 py-3 text-base font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-gray-800 hover:shadow-lg"
            >
              Explore All Datasets
              <FiArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-slate-50 py-24 sm:py-32">
        {/* Background gradient blurs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          {/* Primary orb - left */}
          <div className="absolute -left-20 top-1/2 size-[400px] -translate-y-1/2 rounded-full bg-gradient-to-br from-primary-400/40 to-primary-600/20 blur-3xl" />

          {/* Cyan orb - right */}
          <div className="absolute -right-20 top-1/4 size-[350px] rounded-full bg-gradient-to-bl from-cyan-300/50 to-blue-400/30 blur-3xl" />

          {/* Center blend */}
          <div className="absolute left-1/2 top-1/2 size-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-tr from-violet-200/25 via-primary-200/30 to-cyan-200/25 blur-3xl" />

          {/* Bottom accent */}
          <div className="absolute -bottom-20 right-1/3 h-[300px] w-[400px] rounded-full bg-gradient-to-t from-blue-300/30 to-primary-200/20 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-primary-600">
            Ready to benchmark?
          </p>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl">
            Test your generator&apos;s quality today
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-gray-500">
            Your generator API endpoint just needs to follow our simple
            interface specification. Set up takes minutes, not hours.
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <button
              onClick={handleStartBenchmarking}
              className="group inline-flex items-center gap-2 rounded-full bg-gray-900 px-8 py-4 text-base font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-gray-800 hover:shadow-xl"
            >
              Start Benchmarking
              <FiArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <Link
              to="/generator-api-interface"
              className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-8 py-4 text-base font-semibold text-gray-700 transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-400 hover:shadow-md"
            >
              <FiCode className="size-4" />
              View API Spec
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 bg-gray-50 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <a
              href="https://inceptbench.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 transition-colors hover:text-primary-600"
            >
              InceptBench
              <FiExternalLink className="size-3.5" />
            </a>
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} InceptBench. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

Landing.propTypes = {
  onAuthClick: PropTypes.func.isRequired,
};

export default Landing;
